import {
  Box,
  Card,
  Grid,
  Radio,
  Stack,
  Table,
  Button,
  Dialog,
  Select,
  MenuItem,
  Container,
  TableBody,
  InputLabel,
  RadioGroup,
  Typography,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
  OutlinedInput,
  TableContainer,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
  ReportType,
  ReportFilters,
  ReportFilterKey,
  ReportFilterValue,
  TransactionReportType,
} from 'src/type/report';
import { LoadingButton } from '@mui/lab';
import { useBoolean } from 'src/hooks/use-boolean';
import { toastError } from 'src/utils/format-error';
import axios, { endpoints } from 'src/utils/axios';
import { fParams, fDateRange, ParamsType } from 'src/utils/format-params';
import { enqueueSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import dayjs from 'dayjs';
import { Pagination } from 'src/type/utils';
import usePagination from 'src/hooks/use-pagination';
import TablePaginationCustom from 'src/components/table/table-pagination-custom';
import Scrollbar from 'src/components/scrollbar';
import TableEmptyRows from 'src/components/table/table-empty-rows';
import TableHeadCustom from 'src/components/table/table-head-custom';
import TableLoading from 'src/components/table/table-loading';
import TableNoData from 'src/components/table/table-no-data';
import { emptyRows } from 'src/components/table/utils';
import UserSearchAutocomplete, { UserOption } from 'src/components/ui-kit/user-search-autocomplete';
import { periodTypeOptions, reportTypeOptions } from '../utils';
import ReportWeeklyFilters from '../report-weekly-filters';
import ReportMonthlyFilters from '../report-monthly-filters';
import ReportDailyFilters from '../report-daily-filters';
import ReportPeriodFilters from '../report-period-filters';
import ReportTableRow from '../report-table-row';

const defaultFilters: ReportFilters = {
  reportType: TransactionReportType.ALL,
  filterType: '',
  startDate: null,
  endDate: null,
};

const tableHead = [
  { id: 'invoiceNumber', label: 'ลูกค้า' },
  { id: 'type', label: 'ประเภท', width: 140 },
  { id: 'createDate', label: 'วันที่', width: 220 },
  { id: 'dueDate', label: 'วันที่อนุมัติ', width: 200 },
  // { id: 'sent', label: 'จำนวน', width: 120 },
  { id: 'status', label: 'สถานะ', width: 120 },
];

export default function ReportView() {
  const [filters, setFilters] = useState(defaultFilters);
  const initialize = useBoolean();
  // const userSelect = useBoolean();
  const reload = useBoolean();
  const firstLoad = useBoolean(true);
  const display = useBoolean();
  const isPrinting = useBoolean();
  const printDialogOpen = useBoolean();
  const [printMode, setPrintMode] = useState<'group' | 'individual'>('individual');

  // const [userFilters, setUserFilters] = useState<UserType | null>(null);
  const [userFilters, setUserFilters] = useState<UserOption | null>(null);

  const canReset =
    !!userFilters ||
    !!filters.reportType ||
    !!filters.filterType ||
    (!!filters.startDate && !!filters.endDate);

  const {
    currentPage,
    prevPage,
    rowsPerPage,
    meta,
    onSetData,
    onResetPage,
    onChangePage,
    onChangeRowsPerPage,
    data: tableData,
  } = usePagination<ReportType>();

  useEffect(() => {
    if (firstLoad.value) {
      firstLoad.onFalse();
      return;
    }
    const pageDirection = currentPage > prevPage ? 'next' : 'prev';
    const params = {
      page: currentPage,
      limit: rowsPerPage,
      ...(pageDirection === 'next' && { lastDocId: meta.lastDocId }),
      ...(pageDirection === 'prev' && { firstDocId: meta.firstDocId }),
    };
    handleGetTx(params);
    display.onTrue();
  }, [currentPage, rowsPerPage, reload.value]);

  const handleGetTx = async (params: ParamsType) => {
    try {
      initialize.onTrue();

      const body = {
        type: filters.reportType,
        ...(!!userFilters && { userId: userFilters.value }),
        ...fDateRange(filters.startDate, filters.endDate),
      };

      const { data } = await axios.post<Pagination<ReportType>>(
        endpoints.reports.transactions,
        body,
        {
          params: fParams(params),
        }
      );
      onSetData(data);
    } catch (error) {
      toastError(error);
    } finally {
      initialize.onFalse();
    }
  };

  const handleFilters = (name: ReportFilterKey, value: ReportFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onSelectFilterType = (event: SelectChangeEvent<string>) => {
    handleFilters('filterType', event.target.value as string);

    setFilters((prevState) => ({
      ...prevState,
      filterType: event.target.value,
      startDate: null,
      endDate: null,
    }));
  };

  const handleClear = () => {
    onResetPage();
    setFilters(defaultFilters);
  };

  const handleSubmit = () => {
    onResetPage();
    reload.onToggle();
  };

  const isPrintableReportType =
    filters.reportType === TransactionReportType.BUY_POSITION ||
    filters.reportType === TransactionReportType.SELL_POSITION;

  const downloadReport = async (
    type: TransactionReportType.BUY_POSITION | TransactionReportType.SELL_POSITION,
    startDate: string,
    endDate: string,
    userId?: string,
    mode: 'group' | 'individual' = 'individual'
  ) => {
    try {
      isPrinting.onTrue();

      const params: Record<string, string> = {
        type: type === TransactionReportType.BUY_POSITION ? 'BUY_POSITION' : 'SELL_POSITION',
        startDate,
        endDate,
      };

      // Add groupBy parameter based on mode
      if (mode === 'group') {
        params.groupBy = 'PERSON';
      } else {
        // individual (default) - can omit groupBy or set to TRANSACTION
        params.groupBy = 'TRANSACTION';
      }

      if (userId) {
        params.userId = userId;
      }

      const res = await axios.get(endpoints.printReport.transactions, {
        params,
        responseType: 'blob',
      });

      const contentType = res.headers['content-type'] || '';

      let blob: Blob;
      if (contentType.includes('application/pdf')) {
        // Backend already streams PDF
        blob = res.data as Blob;
      } else if (contentType.includes('application/json')) {
        // Backend returns JSON wrapper { buffer, contentType, filename }
        const text = await (res.data as Blob).text();
        const json = JSON.parse(text);
        // Support Node Buffer object or base64 string
        if (json?.buffer?.type === 'Buffer' && Array.isArray(json.buffer.data)) {
          const bytes = new Uint8Array(json.buffer.data);
          blob = new Blob([bytes], { type: json.contentType || 'application/pdf' });
        } else if (typeof json?.buffer === 'string') {
          const base64 = json.buffer;
          const raw = atob(base64);
          const bytes = new Uint8Array(raw.length);
          for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
          blob = new Blob([bytes], { type: json.contentType || 'application/pdf' });
        } else {
          throw new Error('Invalid JSON payload for PDF');
        }
      } else {
        // Fallback: force as PDF
        blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
      }
      const objUrl = URL.createObjectURL(blob);

      // Open in a new tab using anchor element to avoid popup blocker
      const link = document.createElement('a');
      link.href = objUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar('เตรียมเอกสารสำหรับพิมพ์แล้ว', { variant: 'success' });
    } catch (error: any) {
      if (error?.response?.status === 400) {
        toastError(new Error('ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบวันที่และประเภทรายงาน'));
      } else if (error?.response?.status === 401 || error?.response?.status === 403) {
        toastError(new Error('ไม่มีสิทธิ์ในการพิมพ์รายงาน'));
      } else {
        toastError(error);
      }
    } finally {
      isPrinting.onFalse();
    }
  };

  const handlePrintReport = () => {
    if (!filters.startDate || !filters.endDate) {
      enqueueSnackbar('กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด', { variant: 'error' });
      return;
    }

    printDialogOpen.onTrue();
  };

  const handleConfirmPrint = () => {
    if (!filters.startDate || !filters.endDate) {
      enqueueSnackbar('กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด', { variant: 'error' });
      return;
    }

    const startDate = dayjs(filters.startDate).format('YYYY-MM-DD');
    const endDate = dayjs(filters.endDate).format('YYYY-MM-DD');
    const userId = userFilters?.value;

    downloadReport(
      filters.reportType as
        | TransactionReportType.BUY_POSITION
        | TransactionReportType.SELL_POSITION,
      startDate,
      endDate,
      userId,
      printMode
    );

    printDialogOpen.onFalse();
  };

  const handleCancelPrint = () => {
    printDialogOpen.onFalse();
  };

  // const handleSubmit = async () => {
  //   try {
  //     loading.onTrue();

  //     let reports = Array<ReportType>();
  //     const firstChunk = await getReports();

  //     if (!firstChunk.results.length) {
  //       toastError('ไม่พบข้อมูล');
  //     }

  //     reports = [...firstChunk.results];
  //     const paginateInfo = omit(firstChunk, 'results');

  //     for (let i = 1; i <= paginateInfo.totalPages; i += 1) {
  //       // eslint-disable-next-line no-await-in-loop
  //       const chunk = await getReports(reports[reports.length - 1].id);
  //       reports = [...reports, ...chunk.results];
  //     }
  //   } catch (error) {
  //     toastError(error);
  //   } finally {
  //     loading.onFalse();
  //   }
  // };

  // const getReports = async (params: ParamsType) => {
  //   const body = {
  //     type: filters.reportType,
  //     ...(!!userFilters && { userId: userFilters.uid }),
  //     ...fDateRange(filters.startDate, filters.endDate),
  //   };

  //   const _params = {
  //     limit: 30,
  //     ...params,
  //   };

  //   const { data } = await axios.post<Pagination<ReportType>>(
  //     endpoints.reports.transactions,
  //     body,
  //     {
  //       params: fParams(_params),
  //     }
  //   );
  //   return data;
  // };

  return (
    <>
      <Container>
        <Stack spacing={3}>
          <Typography variant="h4" gutterBottom>
            รายงาน
          </Typography>

          <Grid container>
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                <FormControl
                  sx={{
                    flexShrink: 0,
                    width: 1,
                  }}
                >
                  <InputLabel>ประเภทรายงาน</InputLabel>
                  <Select
                    value={filters.reportType}
                    onChange={(e) => handleFilters('reportType', e.target.value)}
                    input={<OutlinedInput label="ประเภทรายงาน" />}
                  >
                    {reportTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <UserSearchAutocomplete value={userFilters} onChange={setUserFilters} />

                {/* <Box
                  onClick={userSelect.onTrue}
                  sx={{
                    width: 1,
                    height: 54,
                    display: 'flex',
                    alignItems: 'center',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    px: 1.5,
                    cursor: 'pointer',
                  }}
                >
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  {!userFilters ? (
                    <Typography variant="body2" sx={{ color: 'text.disabled', ml: 1 }}>
                      ค้นหาผู้ใช้งาน...
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {userFilters.firstName} {userFilters.lastName}
                    </Typography>
                  )}
                </Box> */}

                <FormControl
                  sx={{
                    flexShrink: 0,
                    width: 1,
                  }}
                >
                  <InputLabel>รูปแบบการกรอง</InputLabel>
                  <Select
                    value={filters.filterType}
                    onChange={onSelectFilterType}
                    input={<OutlinedInput label="รูปแบบการกรอง" />}
                  >
                    {periodTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Grid container>
                  <Grid item xs={12} md={8}>
                    {filters.filterType === 'daily' && (
                      <ReportDailyFilters date={filters.startDate} handleFilters={handleFilters} />
                    )}
                    {filters.filterType === 'weekly' && (
                      <ReportWeeklyFilters date={filters.startDate} handleFilters={handleFilters} />
                    )}
                    {filters.filterType === 'monthly' && (
                      <ReportMonthlyFilters
                        date={filters.startDate}
                        handleFilters={handleFilters}
                      />
                    )}
                    {filters.filterType === 'periot' && (
                      <ReportPeriodFilters
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                        handleFilters={handleFilters}
                      />
                    )}
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          </Grid>

          <Stack spacing={1} direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={1} direction="row" alignItems="center">
              <LoadingButton
                color="inherit"
                variant="contained"
                loading={initialize.value}
                onClick={handleSubmit}
              >
                ค้นหา
              </LoadingButton>
              {canReset && (
                <Box>
                  <Button color="inherit" onClick={handleClear} disabled={initialize.value}>
                    ล้าง
                  </Button>
                </Box>
              )}
            </Stack>

            {isPrintableReportType && (
              <Box>
                <LoadingButton
                  variant="outlined"
                  startIcon={<Iconify icon="solar:printer-bold" />}
                  onClick={handlePrintReport}
                  loading={isPrinting.value}
                  disabled={initialize.value || !filters.startDate || !filters.endDate}
                >
                  พิมพ์รายการ
                </LoadingButton>
              </Box>
            )}
          </Stack>
          {display.value && (
            <Card>
              <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                <Scrollbar>
                  <Table sx={{ minWidth: 800 }}>
                    <TableHeadCustom headLabel={tableHead} />

                    <TableBody>
                      <TableLoading loading={initialize.value} rows={rowsPerPage}>
                        {tableData.map((row) => (
                          <ReportTableRow key={row.id} row={row} />
                        ))}

                        <TableEmptyRows
                          show={!initialize.value}
                          height={76}
                          emptyRows={emptyRows(currentPage, rowsPerPage, meta.totalResults)}
                        />

                        <TableNoData notFound={!tableData.length} />
                      </TableLoading>
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>

              <TablePaginationCustom
                count={meta.totalResults}
                page={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
              />
            </Card>
          )}
        </Stack>
      </Container>
      {/* Print Mode Dialog */}
      <Dialog open={printDialogOpen.value} onClose={handleCancelPrint} maxWidth="sm" fullWidth>
        <DialogTitle>เลือกรูปแบบการพิมพ์</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
            <RadioGroup
              value={printMode}
              onChange={(e) => setPrintMode(e.target.value as 'group' | 'individual')}
            >
              <FormControlLabel value="group" control={<Radio />} label="รวมเป็นรายคน" />
              <FormControlLabel value="individual" control={<Radio />} label="แยกเป็นรายการซื้อ" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancelPrint}>
            ยกเลิก
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleConfirmPrint}
            loading={isPrinting.value}
            disabled={isPrinting.value}
          >
            พิมพ์
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {/* <UserSelectDialog
        open={userSelect.value}
        onClose={userSelect.onFalse}
        onSelect={(user) => setUserFilters(user?.uid)}
      /> */}
    </>
  );
}
